//@ts-strict-ignore
import React, { ReactElement, useContext, useMemo, useState } from 'react';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useHistory } from 'react-router';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import { IFormAction, IFormActions } from '../../@types/entityTypes';
import { useSupplierConfig } from '../../utils/FetchConfig';
import { InfoButton } from '../../components/connectedForm/Button';
import {
  FilterOperation,
  IFilterInput,
  IPurchaseOrder,
  IPurchaseOrderLine,
  PurchaseOrderLineStatus,
  PurchaseOrderMatchingType,
} from '../../graphql/schema/p4types';
import { WorkspaceModalOnly } from '../../components/Workspace/WorkspaceModal';
import ModalContext from '../../components/Context/ModalContext';
import {
  ALLOCATE_RECEIPTS_FIELD_NAMES,
  AllocateReceiptsFilterCondition,
  getAllocateReceiptsFilters,
} from './AllocateReceiptsFilters';

const LazySalesOrderLineWorkspace = React.lazy(() => import('../../views/sales/SalesOrderLineWorkspace'));
const LazyGoodsReceiptLineWorkspace = React.lazy(() => import('../../views/product/GoodsReceiptLineWorkspace'));

export enum PurchaseOrderAction {
  AssignToSales = 'PURCHASE_ASSIGNMENT',
  ReceiveStock = 'PURCHASE_RECEIPT',
  CancelReceipts = 'PURCHASE_CANCEL_RECEIPT',
  AddInvoiceAndMatch = 'SUPPLIER_INVOICES_CREATE',
  AllocateReceipts = 'PURCHASE_ALLOCATE_RECEIPTS',
  LineActions = 'PURCHASE_ORDER_LINE_ACTIONS',
}

export const formActionsForPermissions: IFormActions<PurchaseOrderAction> = [
  { description: 'Assign to Sales/Assemblies', actionReference: PurchaseOrderAction.AssignToSales },
  { description: 'Receive Stock', actionReference: PurchaseOrderAction.ReceiveStock },
  { description: 'Cancel Receipts', actionReference: PurchaseOrderAction.CancelReceipts },
  { description: 'Add Invoice and Match', actionReference: PurchaseOrderAction.AddInvoiceAndMatch },
  { description: 'Allocate Receipts', actionReference: PurchaseOrderAction.AllocateReceipts },
  { description: 'Line Actions', actionReference: PurchaseOrderAction.LineActions },
];

// Action to display a line workspace modal, filtered to only include lines for products which match
// received products on the purchase order and are awaiting allocation
const useAllocateReceipts: () => [IFormAction<PurchaseOrderAction>, JSX.Element] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);

  const receiptLines: Array<IPurchaseOrderLine> = connectedForm?.values?.orderLines;
  const predefinedFilters = getAllocateReceiptsFilters(
    receiptLines,
    AllocateReceiptsFilterCondition.ONLY_RECEIVED_LINES
  );

  return [
    {
      key: 'allocateReceipts',
      text: 'Allocate Receipts',
      condition: () => !!connectedForm.values?.id && connectedForm.values?.status?.includes('RECEIVED'),
      actionReference: PurchaseOrderAction.AllocateReceipts,
      onClick: () => setModalOpen(true),
      'data-testid': 'purchase-order-action-allocate-receipts',
    },
    <WorkspaceModalOnly isOpen={modalOpen}>
      <ModalContext.Provider value={{ onClose: () => setModalOpen(false) }}>
        <LazySalesOrderLineWorkspace
          viewScreenName={'/Customers/Return/'}
          predefinedFilters={predefinedFilters}
          disableViewController={true}
          includeHeader={true}
          removeAddButton={false}
          noForm={true}
          noEdit={true}
          embeddedView={true}
          onBack={() => setModalOpen(false)}
          editInNewTab={true}
          enableRowSelect={true}
          fieldNames={['returned']}
          columnData={ALLOCATE_RECEIPTS_FIELD_NAMES}
        />
      </ModalContext.Provider>
    </WorkspaceModalOnly>,
  ];
};

//Action to display a line workspace modal filtered to this orders' lines.
const useLineActions: () => [IFormAction<PurchaseOrderAction>, JSX.Element] = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);

  const predefinedFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'purchaseOrderLine.purchaseOrder.id',
        value: `${connectedForm.values?.id}`,
      },
    },
  ];

  return [
    {
      key: 'lineActions',
      text: 'Raise Return',
      condition: () =>
        !!connectedForm.values?.id &&
        connectedForm.values?.status?.includes('RECEIVED') &&
        !connectedForm.values?.archived,
      actionReference: PurchaseOrderAction.LineActions,
      onClick: () => setModalOpen(true),
      'data-testid': 'purchase-order-action-raise-return',
    } as IFormAction<PurchaseOrderAction>,
    <WorkspaceModalOnly isOpen={modalOpen}>
      <ModalContext.Provider value={{ onClose: () => setModalOpen(false) }}>
        <LazyGoodsReceiptLineWorkspace
          viewScreenName={'/GoodsReceipt/SupplierReturn/'}
          predefinedFilters={predefinedFilters}
          disableViewController={true}
          includeHeader={true}
          removeAddButton={false}
          noForm={true}
          noEdit={true}
          embeddedView={true}
          onBack={() => setModalOpen(false)}
          editInNewTab={true}
          enableRowSelect={true}
          fieldNames={['returned']}
        />
      </ModalContext.Provider>
    </WorkspaceModalOnly>,
  ];
};

export const usePurchaseOrderFormActions: () => [IFormActions<PurchaseOrderAction>, ReactElement | null] = () => {
  const connectedForm = useContext(ConnectedFormContext);
  const history = useHistory();
  const supplierConfig = useSupplierConfig();
  const [allocateReceipts, allocateReceiptsModals] = useAllocateReceipts();
  const [lineActions, lineActionsModals] = useLineActions();
  const [performSaveThenAssignToSales, confirmationPrompt] = usePerformSave(() => {
    history.push({
      pathname: `/Purchasing/Assignment/Details/${connectedForm.values?.id}`,
      state: { previousLocation: history.location?.pathname || 'back' },
    });
  });

  const archived = !connectedForm.values || connectedForm.values.archived;

  const fullyReceived = useMemo(() => {
    return (
      Array.isArray(connectedForm.values?.orderLines) &&
      connectedForm.values.orderLines.every((orderLine: { received: number; quantity: number }) => {
        return orderLine.received >= orderLine.standardUnitQuantity;
      })
    );
  }, [connectedForm.values?.orderLines]);

  const actions: IFormActions<PurchaseOrderAction> = [
    {
      key: 'assignToSales',
      text: 'Assign to Sales/Assemblies',
      onClick: () => performSaveThenAssignToSales({ submitMeta: { suppressAfterSubmit: true } }),
      actionReference: PurchaseOrderAction.AssignToSales,
      condition: () => !archived,
      'data-testid': 'purchase-order-action-assign-to-sales',
    },
    {
      description: 'Receive Stock',
      btnClass: 'btnInfo',
      individualButton: true,
      component: InfoButton,
      fn: () => {
        history.push({
          pathname: `/Purchasing/Receipt/Details/${connectedForm.values?.id}`,
          state: { previousLocation: history.location?.pathname || 'back' },
        });
      },
      actionReference: PurchaseOrderAction.ReceiveStock,
      condition: (po: IPurchaseOrder) => {
        return !archived && !po.matchPosted && !fullyReceived;
      },
      'data-testid': 'purchase-order-action-receive-stock',
    },
    {
      description: 'Cancel Receipts',
      btnClass: 'btnInfo',
      individualButton: false,
      component: InfoButton,
      fn: () => {
        history.push({
          pathname: `/Purchasing/CancelReceipt/Details/${connectedForm.values?.id}`,
          state: { previousLocation: history.location?.pathname || 'back' },
        });
      },
      actionReference: PurchaseOrderAction.CancelReceipts,
      condition: () => !archived,
      'data-testid': 'purchase-order-action-cancel-receipts',
    },
    {
      description: 'Add Invoice and Match',
      btnClass: 'btnInfo',
      individualButton: false,
      component: InfoButton,
      fn: () => {
        history.push({
          pathname: '/Suppliers/MatchPurchaseOrders/Details/FromPO/New',
          state: {
            modal: true,
            popover: false,
            duplicateId: connectedForm.values?.id, //Actually this is Purchase Order ID to raise the invoice from. Should be okay since purchase invoices have no 'duplicate' option to
            // conflict with.
            previousLocation: history.location?.pathname || 'back',
          },
        });
      },
      actionReference: PurchaseOrderAction.AddInvoiceAndMatch,
      condition: (po: IPurchaseOrder) => {
        return (
          !po.matchPosted &&
          supplierConfig?.purchaseOrderMatchingType === PurchaseOrderMatchingType.PurchaseOrder &&
          (po.status?.findIndex((status: PurchaseOrderLineStatus) => status === 'RECEIVED') ?? -1) >= 0
        );
      },
      'data-testid': 'purchase-order-action-invoice-match',
    },
    allocateReceipts,
    lineActions,
  ];

  return [
    actions,
    <>
      {allocateReceiptsModals}
      {lineActionsModals}
      {confirmationPrompt}
    </>,
  ];
};
